import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal, Button } from 'semantic-ui-react';
import '../../styles/SignatureModal.scss';
import SignatureWrapper from '../../../signature/SignatureWrapper';
import './SignatureV2Modal.scss';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import { useActions } from '../../../hooks/useActions';
import { getSharedFormImageUrl } from '../../actions';
import { RootState } from 'gf-types';
import { useAppSelector } from '../../../hooks/ReduxHooks';
import { ValueChangedForFieldFunc } from '../../formeditor-types';

export interface SignatureV2ModalProps {
  w?: number;
  h?: number;
  name?: string;
  value?: string;
  foregroundColor?: TypeMePlease;
  valueUpdated?: ValueChangedForFieldFunc;
  onClose?: AnyFunc;
  backgroundCropDimensions?: TypeMePlease;
  sasToken?: TypeMePlease;
  fileUrls?: TypeMePlease;
  signingMethod?: number;
  color?: TypeMePlease;
  fieldId?: string;
  formId?: string;
  rowNumber: number;
}

export default function SignatureV2Modal({
  name,
  w,
  h,
  color,
  value,
  valueUpdated,
  onClose,
  formId,
  fieldId,
  backgroundCropDimensions,
  signingMethod,
  rowNumber
}: SignatureV2ModalProps) {
  const [saving, setSaving] = useState(false);
  const [selectedSigningMethod, setSelectedSigningMethod] = useState(signingMethod === 1 ? 1 : 0);
  const isPublicForm = !!useAppSelector<TypeMePlease>((state: RootState) => state.formeditor.sharedAccessToken);
  const [getSharedFormImageUrlAction] = useActions([getSharedFormImageUrl]);
  const windowSize = useWindowDimensions();
  const [selectFontMode, setSelectFontMode] = useState(false);
  const [maxWidth, setMaxWidth] = useState<number>();
  const [maxHeight, setMaxHeight] = useState<number>();
  const contentWrapperRef = useRef<HTMLDivElement>();
  const signatureRef = useRef<TypeMePlease>();
  const valueJson = value && value.indexOf('{') === 0 ? JSON.parse(value) : null;

  useEffect(() => {
    if (valueJson) {
      setSelectedSigningMethod(valueJson.Text ? 1 : 0);
    }
  }, []);

  useLayoutEffect(() => {
    // this is a workaround because the Modal doesn't allow refs
    const modalHeaderRef: HTMLDivElement = document.querySelector('.signaturev2-modal .header');
    const modalContentRef: HTMLDivElement = document.querySelector('.signaturev2-modal .content');
    const modalActionsRef: HTMLDivElement = document.querySelector('.signaturev2-modal .actions');

    const contentComputedSize = window.getComputedStyle(modalContentRef);
    let newMaxWidth = windowSize.width - modalContentRef.offsetLeft - parseInt(contentComputedSize.paddingLeft) - parseInt(contentComputedSize.paddingRight);
    let newMaxHeight = windowSize.height - modalHeaderRef.offsetHeight - modalActionsRef.offsetHeight - 40 - (selectedSigningMethod == 1 ? 41 : 0);

    newMaxWidth = Math.max(Math.min(newMaxWidth, 900), 300);
    newMaxHeight = Math.max(Math.min(newMaxHeight, 600), 100);

    setMaxWidth(newMaxWidth);
    setMaxHeight(newMaxHeight);
  }, [windowSize, contentWrapperRef, selectedSigningMethod]);

  const handleDoneButtonClicked = async () => {
    setSaving(true);
    const newValue = await signatureRef.current.getValue();

    if (isPublicForm && newValue) {
      getSharedFormImageUrlAction(newValue.FileId);
    }
    valueUpdated(newValue ? { value: JSON.stringify(newValue) } : null);
    setSaving(false);

    onClose();
  };

  return (
    <Modal open dimmer={{ inverted: true, className: 'signaturev2-dimmer' }} closeOnDimmerClick={false} className="signaturev2-modal">
      <Modal.Header>
        {name}
        <div className="header-tabs">
          {signingMethod === 1 || signingMethod === 2 ? (
            <button className={`plain-button header-tab${selectedSigningMethod === 1 ? ' selected' : ''}`} onClick={() => setSelectedSigningMethod(1)}>
              Type
            </button>
          ) : null}
          {signingMethod !== 1 ? (
            <button
              className={`plain-button header-tab${selectedSigningMethod === 0 ? ' selected' : ''}`}
              onClick={() => {
                setSelectedSigningMethod(0);
              }}
            >
              Draw
            </button>
          ) : null}
        </div>
      </Modal.Header>
      <Modal.Content>
        <div ref={contentWrapperRef}>
          {contentWrapperRef.current ? (
            <SignatureWrapper
              signingMethod={selectedSigningMethod}
              actualWidth={w}
              actualHeight={h}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              color={color}
              defaultText={valueJson ? valueJson.Text : null}
              defaultFont={valueJson ? valueJson.Font : null}
              defaultData={valueJson ? valueJson.Data : null}
              formId={formId}
              fieldId={fieldId}
              backgroundCropDimensions={backgroundCropDimensions}
              ref={signatureRef}
              showLine={false}
              maxTextLength={100}
              forceUppercase={false}
              rowNumber={rowNumber}
              selectFontMode={selectFontMode}
              setSelectFontMode={setSelectFontMode}
            />
          ) : null}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose()} className="cancel">
          Cancel
        </Button>
        <Button onClick={handleDoneButtonClicked} className="done" loading={saving} disabled={selectFontMode && selectedSigningMethod === 1}>
          Sign
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
